/**
 Copyright Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "..";
import { Types } from "../../constants/types";
import { Hardware } from "~/types/Hardware";
import { hardwareInventoryService } from "~/services";
import { createSocketPatchReducer } from "../events/socketPatch";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

export const hardwareAdapter = createEntityAdapter<Hardware>({
  selectId: (hardware) => hardware.id ?? hardware.hardwareId,
});

interface HardwareState {
  open: boolean;
  export: {
    open: boolean;
    id: string | null;
    isRunning: boolean;
  };
  loading: boolean;
  selectedId: string | null;
  hardwareData: EntityState<Hardware>;
}

const initialState: HardwareState = {
  export: {
    open: false,
    id: null,
    isRunning: false,
  },
  loading: false,
  selectedId: null,
  hardwareData: hardwareAdapter.getInitialState(),
  open: false,
};

export const fetchHardware = createAsyncThunk(
  "vmcHardware/fetchHardware",
  async () => {
    const data = await hardwareInventoryService.getHardwareInventory({
      status: "",
    });

    return data;
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; hardware data is not surfaced in KPI UI
  { condition: () => !isPartnerKpiOnlyUser() },
);

const hardwareSlice = createSlice({
  name: Types.hardware,
  initialState,
  reducers: {
    setCIQExportOpen: (state, action: PayloadAction<boolean>) => {
      state.export.open = action.payload;
    },
    setCIQExportId: (state, action: PayloadAction<string | null>) => {
      state.export.id = action.payload;
    },
    setCIQExportIsRunning: (state, action: PayloadAction<boolean>) => {
      state.export.isRunning = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(hardwareAdapter, (s) => s.hardwareData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHardware.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchHardware.fulfilled, (state, action) => {
      state.loading = false;
      hardwareAdapter.setAll(state.hardwareData, action.payload);
    });
    builder.addCase(fetchHardware.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const hardwareActions = hardwareSlice.actions;
export default hardwareSlice.reducer;

const hardwareEntitySelectors = hardwareAdapter.getSelectors(
  (state: RootState) => state.hardware.hardwareData,
);

export const HardwareData = hardwareEntitySelectors.selectAll;
export const hardwareEntities = hardwareEntitySelectors.selectEntities;

export const HardwareState = createSelector(
  [(state: RootState) => state.hardware.selectedId, hardwareEntities],
  (selectedId, entities): Hardware | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const HardwareOpen = (state: RootState) => state.hardware.open;

/** Lookup by serial (`hardwareId`), memoized via a secondary index. */
export const hardwareByHardwareId = createSelector([HardwareData], (items) => {
  const map: Record<string, Hardware> = {};
  for (const item of items) {
    map[item.hardwareId] = item;
  }
  return map;
});

export const HardwareFromSerialNumber =
  (serial: string) =>
  (state: RootState): Hardware | undefined =>
    serial ? hardwareByHardwareId(state)[serial] : undefined;

export const hardwareExportId = (state: RootState) => state.hardware.export.id;
export const hardwareExportIsRunning = (state: RootState) => state.hardware.export.isRunning;
export const hardwareExportOpen = (state: RootState) => state.hardware.export.open;

export const hardwareLoading = (state: RootState) => state.hardware.loading;
